import {
  ActionIcon,
  Alert,
  Badge,
  Box,
  Button,
  Center,
  FileButton,
  Flex,
  Group,
  List,
  LoadingOverlay,
  ScrollArea,
  Select,
  Stack,
  Text,
  TextInput,
  Tooltip,
} from "@mantine/core";
import { modals } from "@mantine/modals";
import {
  IconAlertTriangle,
  IconCheck,
  IconCloudUpload,
  IconFileSpreadsheet,
  IconPlus,
  IconRefresh,
  IconTrash,
} from "@tabler/icons-react";
import {
  MRT_ColumnDef,
  MantineReactTable,
  useMantineReactTable,
} from "mantine-react-table";
import React, { useCallback, useMemo, useRef, useState } from "react";
import { NotificationExtension } from "../../_base/extension/NotificationExtension";
import { _localization } from "../../config/location";
import {
  ImportExcelColumn,
  ImportExcelRowErrors,
  ImportExcelServerError,
  ImportExcelSubmitResult,
} from "./ImportExcelTypes";
import {
  downloadExcelTemplate,
  readExcelToRows,
  templateColumns,
} from "./importExcelHelper";

type ImportExcelShellProps = {
  /** Cấu hình các cột của file excel và bảng xem trước */
  columns: ImportExcelColumn[];
  /** Tên file excel mẫu khi tải về */
  templateFileName: string;
  /** Dòng dữ liệu ví dụ trên file mẫu */
  sampleRows?: Record<string, any>[];
  /** Chuẩn hoá 1 dòng đọc từ excel thành dòng dữ liệu dùng để xem trước và gửi lên api */
  mapRow: (
    raw: Record<string, any>,
    index: number
  ) => Record<string, any> | Promise<Record<string, any>>;
  /** Dò lại các cột phụ thuộc sau khi người dùng sửa 1 ô (vd: mã chi nhánh -> id, tên chi nhánh) */
  resolveRow?: (
    row: Record<string, any>
  ) => Record<string, any> | Promise<Record<string, any>>;
  /** Kiểm tra dữ liệu, trả về lỗi theo từng dòng / từng cột */
  validateRows: (rows: Record<string, any>[]) => ImportExcelRowErrors;
  /** Gọi api tạo mới hàng loạt */
  onSubmit: (rows: Record<string, any>[]) => Promise<ImportExcelSubmitResult>;
  /** Gọi lại sau khi tạo mới thành công (thường là fetchData của danh sách) */
  onSuccess?: () => void;
  /** Đang tải dữ liệu danh mục phụ trợ (chi nhánh, kho ...) */
  loadingReferenceData?: boolean;
  /** Ghi chú hiển thị ở bước chọn file */
  hint?: React.ReactNode;
  /** Số dòng tối đa cho phép import 1 lần, khớp với giới hạn của api */
  maxRow?: number;
  /**
   * Chiều cao cố định của bảng xem trước, không co giãn theo số dòng của file.
   * Mặc định cao theo màn hình nhưng có chặn trên/dưới để không tràn modal.
   */
  tableHeight?: number | string;
  /**
   * Đóng hộp đang chứa shell. Truyền vào khi shell nằm trong một Modal tự quản
   * (vd mở lồng trong modal khác); bỏ trống thì đóng bằng modals.closeAll() như cũ.
   */
  onClose?: () => void;
  /** Nhãn nút gửi theo số dòng, mặc định "Tạo mới N dòng" */
  submitLabel?: (rowCount: number) => string;
};

const ImportExcelShell = ({
  columns,
  templateFileName,
  sampleRows,
  mapRow,
  resolveRow,
  validateRows,
  onSubmit,
  onSuccess,
  loadingReferenceData,
  hint,
  maxRow = 500,
  tableHeight = "clamp(320px, 62vh, 640px)",
  onClose,
  submitLabel,
}: ImportExcelShellProps) => {
  const [rows, setRows] = useState<Record<string, any>[]>([]);
  const closeModal = () => (onClose ? onClose() : modals.closeAll());
  const [fileName, setFileName] = useState<string>("");
  const [reading, setReading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [serverErrors, setServerErrors] = useState<ImportExcelServerError[]>([]);
  /** Giữ bản mới nhất của rows để cập nhật đồng bộ khi người dùng nhập liên tục */
  const rowsRef = useRef<Record<string, any>[]>([]);
  rowsRef.current = rows;

  const rowErrors: ImportExcelRowErrors = useMemo(
    () => (rows.length > 0 ? validateRows(rows) : {}),
    [rows, validateRows]
  );

  const errorRowCount = useMemo(
    () =>
      Object.values(rowErrors).filter(
        (fields) => fields && Object.keys(fields).length > 0
      ).length,
    [rowErrors]
  );

  const serverErrorByRow = useMemo(() => {
    const map = new Map<number, string>();
    serverErrors.forEach((e) => {
      // api trả về rowIndex bắt đầu từ 1
      map.set(e.rowIndex - 1, e.message);
    });
    return map;
  }, [serverErrors]);

  /**
   * Đọc lỗi qua ref để cấu hình cột của bảng xem trước không bị tạo lại
   * sau mỗi ký tự người dùng nhập (tránh ô nhập bị mất con trỏ).
   */
  const rowErrorsRef = useRef<ImportExcelRowErrors>({});
  const serverErrorByRowRef = useRef<Map<number, string>>(new Map());
  rowErrorsRef.current = rowErrors;
  serverErrorByRowRef.current = serverErrorByRow;

  const handleReadFile = async (file: File | null) => {
    if (!file) return;

    setReading(true);
    setServerErrors([]);
    try {
      const rawRows = await readExcelToRows(file, columns);
      if (rawRows.length === 0) {
        NotificationExtension.Warn("File không có dữ liệu !");
        return;
      }
      if (rawRows.length > maxRow) {
        NotificationExtension.Warn(
          `Chỉ được import tối đa ${maxRow} dòng mỗi lần, file đang có ${rawRows.length} dòng !`
        );
        return;
      }

      const mapped = await Promise.all(
        rawRows.map(async (raw, index) => {
          const row = await mapRow(raw, index);
          return resolveRow ? await resolveRow(row) : row;
        })
      );
      rowsRef.current = mapped;
      setRows(mapped);
      setFileName(file.name);
    } catch (error: any) {
      NotificationExtension.Fails(
        error?.message ??
          "Đọc file excel thất bại, vui lòng kiểm tra lại file !"
      );
    } finally {
      setReading(false);
    }
  };

  const updateCell = useCallback(
    (rowIndex: number, key: string, value: any) => {
      setServerErrors([]);

      const base = rowsRef.current;
      const target = base[rowIndex];
      if (!target) return;

      // Cập nhật ngay giá trị người dùng vừa nhập để ô nhập không bị trễ
      const updated = { ...target, [key]: value };
      const nextRows = base.map((row, index) =>
        index === rowIndex ? updated : row
      );
      rowsRef.current = nextRows;
      setRows(nextRows);

      if (!resolveRow) return;

      // Dò lại các cột phụ thuộc, có thể phải gọi api (vd: lấy phường/xã theo tỉnh)
      Promise.resolve(resolveRow(updated)).then((resolved) => {
        const current = rowsRef.current;
        // Người dùng đã sửa tiếp dòng này thì bỏ qua kết quả cũ
        if (current[rowIndex] !== updated) return;
        const merged = current.map((row, index) =>
          index === rowIndex ? resolved : row
        );
        rowsRef.current = merged;
        setRows(merged);
      });
    },
    [resolveRow]
  );

  const removeRow = useCallback((rowIndex: number) => {
    setServerErrors([]);
    const nextRows = rowsRef.current.filter((_, index) => index !== rowIndex);
    rowsRef.current = nextRows;
    setRows(nextRows);
  }, []);

  const handleSubmit = async () => {
    if (rows.length === 0) {
      NotificationExtension.Warn("Chưa có dữ liệu để tạo mới !");
      return;
    }
    if (errorRowCount > 0) {
      NotificationExtension.Warn(
        `Còn ${errorRowCount} dòng chưa hợp lệ, vui lòng sửa lại trước khi tạo mới !`
      );
      return;
    }

    setSubmitting(true);
    setServerErrors([]);
    try {
      const result = await onSubmit(rows);
      if (result?.success) {
        NotificationExtension.Success(
          result.message ?? `Tạo mới thành công ${rows.length} dòng !`
        );
        onSuccess?.();
        closeModal();
        return;
      }

      if (result?.errors && result.errors.length > 0) {
        setServerErrors(result.errors);
      }

      if (result?.message) {
        NotificationExtension.Fails(result.message);
      } else if (result?.errors && result.errors.length > 0) {
        NotificationExtension.Fails(
          `Tạo mới thất bại, có ${result.errors.length} dòng bị lỗi !`
        );
      } else if (result?.errors) {
        // máy chủ trả về success = false nhưng không kèm thông báo
        NotificationExtension.Fails("Tạo mới thất bại !");
      }
      // result.errors === undefined: lỗi HTTP / mất kết nối, HttpHelper đã hiện thông báo
    } finally {
      setSubmitting(false);
    }
  };

  const previewColumns = useMemo<MRT_ColumnDef<Record<string, any>>[]>(() => {
    const dataColumns: MRT_ColumnDef<Record<string, any>>[] = columns.map(
      (column) => ({
        accessorKey: column.key,
        header: column.header,
        size: column.size ?? 160,
        enableSorting: false,
        enableColumnActions: false,
        enableColumnFilter: false,
        Cell: ({ row }) => {
          const value = row.original[column.key];
          const error = rowErrorsRef.current[row.index]?.[column.key];

          if (column.editable === false) {
            return (
              <Tooltip label={error} disabled={!error} color="red">
                <Text size="12.5px" c={error ? "red" : undefined}>
                  {column.type === "boolean"
                    ? value
                      ? column.booleanLabels?.trueLabel ?? "Đang sử dụng"
                      : column.booleanLabels?.falseLabel ?? "Dừng sử dụng"
                    : value ?? ""}
                </Text>
              </Tooltip>
            );
          }

          if (column.type === "select" || column.type === "boolean") {
            const options =
              column.type === "boolean"
                ? [
                    {
                      value: "true",
                      label: column.booleanLabels?.trueLabel ?? "Đang sử dụng",
                    },
                    {
                      value: "false",
                      label: column.booleanLabels?.falseLabel ?? "Dừng sử dụng",
                    },
                  ]
                : typeof column.options === "function"
                ? column.options(row.original)
                : column.options ?? [];

            return (
              <Tooltip
                label={error}
                disabled={!error}
                color="red"
                position="top-start"
              >
                <Select
                  size="xs"
                  variant="unstyled"
                  searchable
                  allowDeselect={false}
                  comboboxProps={{ withinPortal: true }}
                  placeholder={column.placeholder ?? "Chọn"}
                  data={options}
                  value={
                    column.type === "boolean"
                      ? value === null || value === undefined
                        ? null
                        : String(Boolean(value))
                      : value === null || value === undefined
                      ? null
                      : String(value)
                  }
                  // chỉ tô đỏ, nội dung lỗi xem ở cột "Lỗi" để dòng không bị cao lên
                  error={Boolean(error)}
                  nothingFoundMessage="Không tìm thấy dữ liệu !"
                  onChange={(selected) =>
                    updateCell(
                      row.index,
                      column.key,
                      column.type === "boolean" ? selected === "true" : selected
                    )
                  }
                />
              </Tooltip>
            );
          }

          return (
            <Tooltip
              label={error}
              disabled={!error}
              color="red"
              position="top-start"
            >
              <TextInput
                size="xs"
                variant="unstyled"
                placeholder={column.placeholder ?? column.header}
                value={value ?? ""}
                error={Boolean(error)}
                onChange={(e) =>
                  updateCell(row.index, column.key, e.currentTarget.value)
                }
              />
            </Tooltip>
          );
        },
      })
    );

    return [
      {
        accessorKey: "__rowIndex",
        header: "STT",
        size: 60,
        enableSorting: false,
        enableColumnActions: false,
        enableColumnFilter: false,
        Cell: ({ row }) => {
          const hasError =
            Object.keys(rowErrorsRef.current[row.index] ?? {}).length > 0 ||
            serverErrorByRowRef.current.has(row.index);
          return (
            <Badge
              radius="sm"
              variant="light"
              color={hasError ? "red" : "teal"}
              size="sm"
            >
              {row.index + 1}
            </Badge>
          );
        },
      },
      ...dataColumns,
      {
        accessorKey: "__error",
        header: "Lỗi",
        size: 240,
        enableSorting: false,
        enableColumnActions: false,
        enableColumnFilter: false,
        Cell: ({ row }) => {
          const messages = [
            ...Object.values(rowErrorsRef.current[row.index] ?? {}),
            ...(serverErrorByRowRef.current.has(row.index)
              ? [serverErrorByRowRef.current.get(row.index) as string]
              : []),
          ];
          if (messages.length === 0)
            return (
              <Text size="12.5px" c="teal">
                Hợp lệ
              </Text>
            );
          return (
            <Text size="12.5px" c="red">
              {messages.join("; ")}
            </Text>
          );
        },
      },
      {
        accessorKey: "__action",
        header: "Thao tác",
        size: 90,
        enableSorting: false,
        enableColumnActions: false,
        enableColumnFilter: false,
        Cell: ({ row }) => (
          <Tooltip label="Xoá dòng này">
            <ActionIcon
              variant="light"
              color="red"
              onClick={() => removeRow(row.index)}
            >
              <IconTrash size={18} stroke={1.5} />
            </ActionIcon>
          </Tooltip>
        ),
      },
    ];
  }, [columns, updateCell, removeRow]);

  const table = useMantineReactTable({
    columns: previewColumns,
    data: rows,
    localization: _localization,
    getRowId: (_row, index) => String(index),
    enableTopToolbar: false,
    enableRowSelection: false,
    enableColumnResizing: true,
    enableStickyHeader: true,
    enableColumnPinning: true,
    initialState: {
      density: "xs",
      columnPinning: {
        left: ["__rowIndex"],
        right: ["__error", "__action"],
      },
      pagination: { pageIndex: 0, pageSize: 50 },
    },
    mantineTableContainerProps: {
      // Fix cứng chiều cao để modal không co giãn theo số dòng của file
      style: {
        height: tableHeight,
        minHeight: tableHeight,
        maxHeight: tableHeight,
      },
    },
    mantineTableHeadCellProps: ({ column }) => ({
      style: {
        fontSize: "12.5px",
        // Cột ghim phải có nền đục, nếu không sẽ nhìn thấy các cột bên dưới trượt qua
        backgroundColor: column.getIsPinned()
          ? "var(--mantine-color-body, #fff)"
          : undefined,
      },
    }),
    mantineTableBodyCellProps: ({ column }) => ({
      style: {
        fontSize: "12.5px",
        padding: "2px 8px",
        backgroundColor: column.getIsPinned()
          ? "var(--mantine-color-body, #fff)"
          : undefined,
        opacity: 1,
      },
    }),
    mantinePaginationProps: {
      showRowsPerPage: true,
      withEdges: true,
      rowsPerPageOptions: ["50", "100", "200"],
    },
    paginationDisplayMode: "pages",
    mantineTableProps: { striped: false, highlightOnHover: false },
  });

  /** Thêm 1 dòng trống để tự nhập tay, dùng chính mapRow nên giá trị mặc định giống import */
  const addRow = async () => {
    if (rowsRef.current.length >= maxRow) {
      NotificationExtension.Warn(`Chỉ được tạo tối đa ${maxRow} dòng mỗi lần !`);
      return;
    }

    setServerErrors([]);
    const row = await mapRow({}, rowsRef.current.length);
    const resolved = resolveRow ? await resolveRow(row) : row;

    const nextRows = [...rowsRef.current, resolved];
    rowsRef.current = nextRows;
    setRows(nextRows);

    // Nhảy tới trang chứa dòng vừa thêm
    const pageSize = table.getState().pagination.pageSize;
    table.setPageIndex(Math.floor((nextRows.length - 1) / pageSize));
  };

  if (rows.length === 0) {
    return (
      <Box style={{ position: "relative" }} w={{ base: "100%", md: 620 }}>
        <LoadingOverlay visible={reading || Boolean(loadingReferenceData)} />
        <Stack gap="sm" py="md">
          <Center>
            <Stack align="center" gap={6}>
              <IconCloudUpload size={46} stroke={1.2} color="#15aabf" />
              <Text fw={500}>Chọn file excel cần import</Text>
              <Text size="xs" c="dimmed">
                Định dạng .xlsx, .xls - tối đa {maxRow} dòng mỗi lần
              </Text>
              <Text size="xs" c="dimmed">
                Hoặc bấm <b>Tự nhập tay</b> để nhập trực tiếp trên bảng
              </Text>
            </Stack>
          </Center>

          {hint ? (
            <Alert
              variant="light"
              color="blue"
              icon={<IconFileSpreadsheet size={18} />}
            >
              {hint}
            </Alert>
          ) : null}

          <Alert variant="light" color="gray">
            <Text size="xs" fw={500} mb={4}>
              Các cột trên file excel:
            </Text>
            <ScrollArea.Autosize mah={140}>
              <List size="xs" spacing={2}>
                {templateColumns(columns).map((c) => (
                  <List.Item key={c.key}>
                    {c.header}
                    {c.required ? (
                      <Text span c="red">
                        {" "}
                        (*)
                      </Text>
                    ) : null}
                    {Array.isArray(c.options) && c.options.length > 0 ? (
                      <Text span c="dimmed">
                        {" "}
                        - {c.options.map((o) => o.label).join(" | ")}
                      </Text>
                    ) : c.description ? (
                      <Text span c="dimmed">
                        {" "}
                        - {c.description}
                      </Text>
                    ) : null}
                  </List.Item>
                ))}
              </List>
            </ScrollArea.Autosize>
          </Alert>

          <Group justify="center" gap="sm">
            <Button
              variant="subtle"
              leftSection={<IconPlus size={16} />}
              onClick={addRow}
            >
              Tự nhập tay
            </Button>
            <Button
              variant="outline"
              leftSection={<IconFileSpreadsheet size={16} />}
              onClick={() =>
                downloadExcelTemplate(columns, templateFileName, sampleRows)
              }
            >
              Tải file mẫu
            </Button>
            <FileButton
              onChange={handleReadFile}
              accept=".xlsx,.xls,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel"
            >
              {(props) => (
                <Button
                  {...props}
                  leftSection={<IconCloudUpload size={16} />}
                  loading={reading}
                >
                  Chọn file
                </Button>
              )}
            </FileButton>
          </Group>
        </Stack>
      </Box>
    );
  }

  return (
    <Box style={{ position: "relative" }} w={{ base: "100%", md: "88vw" }}>
      <LoadingOverlay visible={submitting || reading} />

      {/* Dùng padding thay margin để không bị margin-collapse với body của modal */}
      <Flex
        justify="space-between"
        align="center"
        wrap="wrap"
        gap="sm"
        style={{ paddingTop: 12, paddingBottom: 24, marginBottom: 8 }}
      >
        <Group gap="xs">
          <IconFileSpreadsheet size={18} color="#15aabf" />
          <Text size="sm" fw={500}>
            {fileName || "Nhập tay trên bảng"}
          </Text>
          <Badge variant="light" color="blue" size="sm">
            {rows.length} dòng
          </Badge>
          {errorRowCount > 0 ? (
            <Badge variant="light" color="red" size="sm">
              {errorRowCount} dòng lỗi
            </Badge>
          ) : (
            <Badge variant="light" color="teal" size="sm">
              Dữ liệu hợp lệ
            </Badge>
          )}
        </Group>

        <Group gap="xs">
          <Button
            size="xs"
            variant="light"
            leftSection={<IconPlus size={14} />}
            onClick={addRow}
          >
            Thêm dòng
          </Button>
          <Button
            size="xs"
            variant="outline"
            leftSection={<IconFileSpreadsheet size={14} />}
            onClick={() =>
              downloadExcelTemplate(columns, templateFileName, sampleRows)
            }
          >
            Tải file mẫu
          </Button>
          <FileButton
            onChange={handleReadFile}
            accept=".xlsx,.xls,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel"
          >
            {(props) => (
              <Button
                {...props}
                size="xs"
                variant="outline"
                color="violet"
                leftSection={<IconRefresh size={14} />}
              >
                Chọn lại file
              </Button>
            )}
          </FileButton>
        </Group>
      </Flex>

      {errorRowCount > 0 ? (
        <Alert
          variant="light"
          color="red"
          icon={<IconAlertTriangle size={18} />}
          mb="md"
          p={8}
        >
          <Text size="xs">
            Có {errorRowCount} dòng chưa hợp lệ. Bạn có thể sửa trực tiếp trên
            bảng dưới đây hoặc xoá dòng đó rồi tạo mới.
          </Text>
        </Alert>
      ) : null}

      {serverErrors.length > 0 ? (
        <Alert
          variant="light"
          color="red"
          icon={<IconAlertTriangle size={18} />}
          mb="md"
          p={8}
        >
          <Text size="xs" fw={500} mb={4}>
            Máy chủ từ chối dữ liệu, chưa có dòng nào được tạo mới:
          </Text>
          <ScrollArea.Autosize mah={110}>
            <List size="xs" spacing={2}>
              {serverErrors.map((e, index) => (
                <List.Item key={`${e.rowIndex}-${index}`}>
                  Dòng {e.rowIndex}
                  {e.code ? ` (${e.code})` : ""}: {e.message}
                </List.Item>
              ))}
            </List>
          </ScrollArea.Autosize>
        </Alert>
      ) : null}

      <MantineReactTable table={table} />

      <Group
        justify="flex-end"
        mt="md"
        mb="xs"
        style={{ position: "sticky", bottom: 0, backgroundColor: "white" }}
      >
        <Button variant="default" onClick={closeModal}>
          Đóng
        </Button>
        <Button
          leftSection={<IconCheck size={16} />}
          loading={submitting}
          disabled={errorRowCount > 0}
          onClick={handleSubmit}
        >
          {submitLabel
            ? submitLabel(rows.length)
            : `Tạo mới ${rows.length} dòng`}
        </Button>
      </Group>
    </Box>
  );
};

export default ImportExcelShell;
